import { ALL_TIME_START_BEFORE_ANY_RECORD, dashboardWidget, type DashboardDateRange } from "../dashboard";
import { countNote, DASHBOARD_COPY } from "../dashboard-copy";
import { fillTemplate } from "../../values/fill-template";
import type { DashboardWidgetData } from "../dashboard-data";
import { ORDER_DIRECTIONS, ORDER_STATUSES, type OrderDirection, type OrderStatus } from "../../orders/order-types";
import { orderValue, roundMoney } from "../../orders/order-money";
import { isCancelledOrder, isPendingOrder, orderMatchesView } from "../../orders/order-views";
import { bar, byLabel, coverageOf, inRange, stat, sumBy, type AreaSummary } from "./build";
import { joinPointKey } from "./point-key";
import { balanceStat, priceCoverage, valueStat, type PricedOrder } from "./money";

export type DashboardOrder = PricedOrder & {
  orderStatus: OrderStatus;
  direction: OrderDirection;
  submittedAt: Date | string;
  orderedAt?: Date | string | null;
  arrivedDate?: Date | string | null;
};

export type OrdersInput = { orders: readonly DashboardOrder[] };

type OrderDateField = keyof typeof DASHBOARD_COPY.orderDates;
type OrderSummary = (orders: readonly DashboardOrder[]) => DashboardWidgetData;

function isPending(order: DashboardOrder): boolean {
  return isPendingOrder({ status: order.orderStatus, direction: order.direction });
}

function withBalance(orders: readonly DashboardOrder[], direction: OrderDirection): DashboardOrder[] {
  return orders.filter((order) => order.direction === direction && order.orderStatus === ORDER_STATUSES.fulfilled);
}

function isOpen(order: DashboardOrder): boolean {
  return orderMatchesView({ status: order.orderStatus, direction: order.direction }, "openSales");
}

function validOrderDate(date: Date | string | null | undefined): date is Date | string {
  return date != null && Number.isFinite(new Date(date).getTime());
}

function dateMatches(order: DashboardOrder, field: OrderDateField, range: DashboardDateRange): boolean {
  const date = order[field];
  return validOrderDate(date) ? inRange(date, range) : range.start === ALL_TIME_START_BEFORE_ANY_RECORD;
}

function withDateCoverage(data: DashboardWidgetData, missing: number, field: OrderDateField, range: DashboardDateRange): DashboardWidgetData {
  if (missing === 0) return data;
  const note = countNote(DASHBOARD_COPY.notes.missingOrderDate, missing, { date: DASHBOARD_COPY.orderDates[field] });
  const coverage = coverageOf(data.coverage.complete && range.start === ALL_TIME_START_BEFORE_ANY_RECORD, [data.coverage.note ?? null, note]);
  return { ...data, coverage: { ...data.coverage, ...coverage } };
}

function periodSummary(orders: readonly DashboardOrder[], range: DashboardDateRange, key: string, summarize: OrderSummary): DashboardWidgetData {
  const field = dashboardWidget(key).orderDateField;
  if (!field) throw new Error(fillTemplate(DASHBOARD_COPY.catalogErrors.orderDateFieldMissing, { key }));
  const selected = orders.filter((order) => dateMatches(order, field, range));
  const missing = orders.filter((order) => !validOrderDate(order[field])).length;
  return withDateCoverage(summarize(selected), missing, field, range);
}

function pendingCount(orders: readonly DashboardOrder[]): DashboardWidgetData {
  return stat(orders.length);
}

function yearAndDirection(order: DashboardOrder): string {
  const year = validOrderDate(order.submittedAt) ? String(new Date(order.submittedAt).getUTCFullYear()) : DASHBOARD_COPY.unknown;
  return joinPointKey(year, order.direction);
}

function valueByYear(orders: readonly DashboardOrder[]) {
  const priced = orders.filter((order) => orderValue(order.pricePerItem, order.quantity) != null);
  const points = sumBy(priced, yearAndDirection, (order) => orderValue(order.pricePerItem, order.quantity)!)
    .map((point) => ({ ...point, value: roundMoney(point.value) }))
    .sort(byLabel);
  return bar(points, priceCoverage(orders.length - priced.length));
}

export function summarizeOrders({ orders }: OrdersInput, range: DashboardDateRange): AreaSummary {
  const live = orders.filter((order) => !isCancelledOrder({ status: order.orderStatus }));
  return {
    pendingOrders: periodSummary(orders.filter(isPending), range, "pendingOrders", pendingCount),
    openOrdersValue: periodSummary(orders.filter(isOpen), range, "openOrdersValue", valueStat),
    unpaidSaleBalances: periodSummary(withBalance(orders, ORDER_DIRECTIONS.sale), range, "unpaidSaleBalances", balanceStat),
    unpaidReceivedPurchases: periodSummary(withBalance(orders, ORDER_DIRECTIONS.purchase), range, "unpaidReceivedPurchases", balanceStat),
    orderValueByYear: periodSummary(live, range, "orderValueByYear", valueByYear),
  };
}
